import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@/components/Button';
import BasicInput from '@/components/BasicInput';
import useCountdown from '@/hooks/useCountdown';
import changeEmailIcon from '@/assets/change_email.svg';
import changeSuccessIcon from '@/assets/change_success.svg';
import { sendEmailVerificationForChangeEmail } from '@/api/profile';
import { changeEmail } from '@/api/auth';
import { useToast } from '@/components/ToastProvider';
import { validateEmail } from '@/utils/validationUtils';

const ChangeEmailDialog = ({ onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { timeLeft, isActive, start } = useCountdown(60);

  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!email) {
      newErrors.email = t('user.errors.email_required');
    } else if (!validateEmail(email)) {
      newErrors.email = t('user.errors.email_invalid');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const validateVerificationForm = () => {
    const newErrors = {};

    if (!verificationCode) {
      newErrors.verificationCode = t('user.errors.verification_code_required');
    } else if (verificationCode.length !== 6) {
      newErrors.verificationCode = t('user.errors.verification_code_invalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerificationCodeChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(numericValue);
  };
  
  const handleSendVerification = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await sendEmailVerificationForChangeEmail(email);
      setIsEmailSent(true);
      start(); // Start countdown when verification code is sent
    } catch (error) {
      console.error('Email verification request failed:', error);
      if (error.response?.data?.code === 'user_already_exists') {
        setErrors({
          email: t('user.errors.email_already_exists')
        });
      } else {
        setErrors({
          email: t('user.errors.email_send_failed')
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleVerifyAndChange = async () => {
    if (!validateVerificationForm()) {
      return;
    }
    
    setIsLoading(true);
    try {
      await changeEmail(email, verificationCode);
      setIsSuccess(true);
      // Invalidate profile data to refresh it
      queryClient.invalidateQueries(['user-profile']);
    } catch (error) {
      console.error('Email change failed:', error);
      setErrors({
        verificationCode: t('user.errors.verification_code_invalid')
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      await sendEmailVerificationForChangeEmail(email);
      toast(t('user.email.verification_resent'));
      start(); // Start countdown after resending
    } catch (error) {
      console.error('Resend verification failed:', error);
      if (error.response?.data?.code === 'verification_code_rate_limited') {
        toast(t('user.errors.verification_code_rate_limited'));
      } else if (error.response?.data?.code === 'user_already_exists') {
        toast(t('user.errors.email_already_exists'));
      } else {
        toast(t('user.errors.email_send_failed'));
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleClose = () => {
    onClose();
  };
  
  // Initial email input view
  if (!isEmailSent && !isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="w-[360px] bg-white rounded-xl inline-flex flex-col justify-start items-center overflow-hidden">
          <div className="self-stretch px-6 pt-8 pb-6 flex flex-col justify-start items-center gap-4">
            <div className="w-12 h-12 relative">
              <img src={changeEmailIcon} alt="Change Email" className="w-full h-full" />
            </div>
            <div className="w-[263px] flex flex-col justify-center items-center gap-2">
              <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
                {t('user.email.new_email_title')}
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-1">
              <BasicInput
                placeholder={t('user.email.email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                containerClassName="self-stretch"
                className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                maxLength={40}
              />
            </div>
          </div>
          <div className="self-stretch px-6 pb-6 bg-white flex flex-col justify-center items-center">
            <div className="self-stretch flex items-center gap-3">
              <Button 
                variant="default" 
                className="flex-1 h-12 px-6 bg-white rounded-3xl outline outline-[0.5px] outline-offset-[-0.5px] outline-[#dddddd]"
                onClick={handleClose}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                variant="primary" 
                className="flex-1 h-12 px-6 bg-[#111111] rounded-3xl"
                onClick={handleSendVerification}
                disabled={isLoading}
              >
                {isLoading ? t('common.loading') : t('user.email.send_verification')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Verification code view
  if (isEmailSent && !isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="w-[360px] bg-white rounded-xl inline-flex flex-col justify-start items-center overflow-hidden">
          <div className="self-stretch px-6 pt-8 pb-6 flex flex-col justify-start items-center gap-4">
            <div className="w-12 h-12 relative">
              <img src={changeEmailIcon} alt="Change Email" className="w-full h-full" />
            </div>
            <div className="w-[263px] flex flex-col justify-center items-center gap-2">
              <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
                {t('user.email.verification_sent_title')}
              </div>
              <div className="self-stretch text-center text-[#666666] text-sm font-normal leading-[21px]">
                {email}
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-1">
              <BasicInput
                placeholder={t('user.email.verification_code_placeholder')}
                value={verificationCode}
                onChange={handleVerificationCodeChange}
                error={errors.verificationCode}
                containerClassName="self-stretch"
                className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
              />
              <div className="self-stretch pt-0.5 inline-flex justify-start items-center gap-1">
                <div>
                  {isActive ? (
                    <span className="text-[#aaaaaa] text-xs font-normal leading-[18px]">
                      {t('auth.signup.verification.no_code_countdown', { seconds: timeLeft })}
                    </span>
                  ) : (
                    <>
                      <span className="text-[#aaaaaa] text-xs font-normal leading-[18px]">
                        {t('user.email.no_code')}
                      </span>
                      <span
                        className="text-[#2966ff] text-xs font-normal leading-[18px] cursor-pointer ml-1"
                        onClick={handleResendCode}
                      >
                        {t('user.email.resend')}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="self-stretch px-6 py-4 bg-[#f7f7f7] rounded-md flex flex-col justify-start items-start gap-3">
              <div className="self-stretch text-[#333333] text-xs font-bold">
                {t('user.email.help_title')}
              </div>
              <div className="self-stretch">
                <span className="text-[#999999] text-xs font-normal">
                  {t('user.email.help_text')}
                </span>
                <span className="text-[#2966ff] text-xs font-normal underline cursor-pointer">
                  {t('user.email.contact_us')}
                </span>
              </div>
            </div>
          </div>
          <div className="self-stretch px-6 pb-6 bg-white flex flex-col justify-center items-center">
            <div className="self-stretch flex items-center gap-3">
              <Button 
                variant="default" 
                className="flex-1 h-12 px-6 bg-white rounded-3xl outline outline-[0.5px] outline-offset-[-0.5px] outline-[#dddddd]"
                onClick={handleClose}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                className="flex-1 h-12 px-6 bg-[#111111] rounded-3xl"
                onClick={handleVerifyAndChange}
                disabled={!verificationCode || verificationCode.length !== 6 || isLoading}
              >
                {isLoading ? t('common.loading') : t('common.next')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Success view
  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="w-[360px] bg-white rounded-xl inline-flex flex-col justify-start items-center overflow-hidden">
          <div className="self-stretch px-6 pt-8 pb-6 flex flex-col justify-start items-center gap-4">
            <div className="w-12 h-12 relative">
              <img src={changeSuccessIcon} alt="Success" className="w-full h-full" />
            </div>
            <div className="w-[263px] flex flex-col justify-center items-center gap-2">
              <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
                {t('user.email.success_title')}
              </div>
              <div className="self-stretch text-center text-[#666666] text-sm font-normal leading-[21px]">
                {t('user.email.success_message', { email })}
              </div>
            </div>
          </div>
          <div className="px-6 pb-6 bg-white flex flex-col justify-center items-center">
            <div className="self-stretch inline-flex justify-center items-center">
              <Button 
                variant="primary" 
                className="w-[101.50px] h-12 px-6 bg-[#111111] rounded-3xl"
                onClick={handleClose}
              >
                {t('common.confirm')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default ChangeEmailDialog; 